import React from "react";
import { Link } from "react-router-dom";
import { ReactComponent as LinkedIn } from "../assets/svgs/linkedIn.svg";
import { ReactComponent as Instagram } from "../assets/svgs/instagram.svg";

interface FooterProps {
    copyrightText: string;
    logo: string;
    config?: {
        copyrightText?: string;
        locationHeading?: string;
        address?: string;
        addressHref?: string;
        conversationHeading?: string;
        email?: string;
        phone?: string;
        instagramHref?: string;
        linkedinHref?: string;
        sealImageUrl?: string;
        sealHref?: string;
        privacyLabel?: string;
        privacyHref?: string;
    };
}

const Footer: React.FC<FooterProps> = ({ copyrightText, logo, config }) => {
    const addressLines = (config?.address || '').split('\n').filter(Boolean);
    const email = config?.email || '';
    const phone = config?.phone || '';
    const telHref = `tel:${phone.replace(/[^\d+]/g, '')}`;
    const sealUrl = config?.sealImageUrl || '';
    const privacyLabel = config?.privacyLabel || '';
    const privacyHref = config?.privacyHref || '';

    return (
        <div>
            <footer className="bg-ekc-blue font-jakarta">
                <div className="container pb-8 pt-32 max-w-7xl m-auto">
                    <div className="gap-1 md:gap-8 grid grid-cols-[repeat(auto-fill,215px)]">
                        <div className="flex justify-center items-center flex-col">
                            <Link to="/">
                                <picture>
                                    <source srcSet={logo} type="image/webp" />
                                    <img src={logo} alt="logo" className='max-h-5 sm:max-h-10 drop-shadow-md' />
                                </picture>
                            </Link>
                            {sealUrl ? (
                                <a href={config?.sealHref || sealUrl} target="_blank" rel="noreferrer">
                                    <img src={sealUrl} alt="USFCR Verified Vendor" title="US Federal Contractor Registration System for Award Management Verified Vendor Seal" className='mt-4 w-24 drop-shadow-md' />
                                </a>
                            ) : null}
                        </div>
                        <div className="mt-16 gap-8 xl:mt-0 xl:mx-auto">
                            <h3 className="text-white font-semibold text-xl">{config?.locationHeading}</h3>
                            <ul className="list-none mt-6 space-y-2 text-white">
                                {addressLines.length ? (
                                    <li>
                                        {config?.addressHref ? (
                                            <a href={config.addressHref} target="_blank" rel="noreferrer" className="text-white hover:text-gray-300">
                                                {addressLines.map((line, index) => (
                                                    <React.Fragment key={line}>
                                                        {line}{index < addressLines.length - 1 ? <br /> : null}
                                                    </React.Fragment>
                                                ))}
                                            </a>
                                        ) : (
                                            addressLines.map((line, index) => (
                                                <React.Fragment key={line}>
                                                    {line}{index < addressLines.length - 1 ? <br /> : null}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </li>
                                ) : null}
                            </ul>
                        </div>
                        <div className="mt-16 gap-8 xl:mt-0 xl:mx-auto">
                            <h3 className="text-white font-semibold text-xl">{config?.conversationHeading}</h3>
                            <ul className="list-none mt-6 space-y-2 text-white">
                                {email ? <li className="hover:text-gray-300"><a href={`mailto:${email}`}>{email}</a></li> : null}
                                {phone ? <li className="hover:text-gray-300"><a href={telHref}>{phone}</a></li> : null}
                                <li className="flex !mt-8">
                                    {config?.instagramHref ? (
                                        <a href={config.instagramHref} target="_blank" rel="noreferrer" aria-label="Instagram">
                                            <Instagram className="cursor-pointer w-6 h-6 fill-white" />
                                        </a>
                                    ) : null}
                                    {config?.linkedinHref ? (
                                        <a className="ml-6" href={config.linkedinHref} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                                            <LinkedIn className="cursor-pointer w-6 h-6 fill-white" />
                                        </a>
                                    ) : null}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="text-white border-t-2 pt-8 mt-16 border-white/[0.1] border-solid text-xs sm:mt-20 lg:mt-24">
                        <p>
                            {config?.copyrightText || copyrightText}
                            {privacyLabel && privacyHref ? <> | <Link to={privacyHref}>{privacyLabel}</Link></> : null}
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Footer;
